import { ReactElement, useEffect, useMemo } from 'react';
import { useParams, useLocation, Outlet, useNavigate } from 'react-router-dom';
import SectionHeader from '../../components/SectionHeader';
import TabMenu from '../../components/TabMenu';
import { useRunAndCalibration } from '../../hooks/useRuns';
import { useSessionCalcData } from '../../hooks/useSessions';
import RunActions from './RunActions';
import DownloadCSVButton from './DownloadCSVButton';
import DownloadFITButton from './DownloadFITButton';
import RunSummary from './RunDetails/RunSummary';
import { hasAuxSensors } from './RunAuxSensor/auxSensorChannels';
import { HiChevronLeft, HiChevronRight } from 'react-icons/hi2';
import { RUN_TABS, RUN_SUBROUTE_PATHS } from './runTabs';
import { sessionIdFromPath } from '../../utils/sessionIdFromPath';
import { useFeedbackPageContext } from '../../hooks/useFeedbackPageContext';

const LAST_VISITED_TAB_KEY = 'runViewLastTab';

export default function RunView(): ReactElement {
  const { runId, sessionId } = useParams<{ runId: string; sessionId?: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const { isPending: isRunsPending, isFetching: isRunsFetching, data } = useRunAndCalibration(runId || '');
  const run = data?.run;
  const calibration = data?.calibration;

  // Fetch session data if we're in a session context to get run navigation
  const extractedSessionId = useMemo(
    () => sessionId ?? sessionIdFromPath(location.pathname),
    [sessionId, location.pathname],
  );

  const { data: sessionData } = useSessionCalcData(extractedSessionId || 'no-session-selected');

  // Check if we're in a session context
  const isInSessionContext =
    !!sessionId || (location.pathname.includes('/sessions/') && location.pathname.includes('/run/'));

  const getBackLink = () => {
    const navState = location.state as { backTo?: string; backText?: string } | null;
    if (navState?.backTo) {
      return { to: navState.backTo, text: navState.backText || 'Back' };
    }
    if (isInSessionContext) {
      if (sessionId) {
        return { to: `/sessions/${sessionId}`, text: 'Back to Session' };
      } else {
        // Extract sessionId from URL if not in params
        const urlSessionId = sessionIdFromPath(location.pathname);
        if (urlSessionId) {
          return { to: `/sessions/${urlSessionId}`, text: 'Back to Session' };
        }
      }
    }
    return { to: '/runs', text: 'Back to Runs' };
  };

  const getBasePath = () => {
    if (isInSessionContext) {
      if (sessionId) {
        return `/sessions/${sessionId}/runs/${runId}`;
      } else {
        // Extract sessionId from URL if not in params
        const urlSessionId = sessionIdFromPath(location.pathname);
        if (urlSessionId) {
          return `/sessions/${urlSessionId}/runs/${runId}`;
        }
      }
    }
    return `/runs/${runId}`;
  };

  // Show the Aux Sensor tab only when more than one power meter, speed, or wind
  // sensor was attached (detected from run_health_obj.sensors_present — no extra
  // fetch). It sits between Plotter and Health Check.
  const showAuxSensor = hasAuxSensors(run);

  // Show the High Rate tab only when the pipeline stored a 20 Hz HTS grid
  // (has_hts rides along on the basic-plot payload — no extra fetch).
  const showHts = !!run?.has_hts;

  // Published for feedback reports filed from this page — ids and which tab
  // was open, not the run payload.
  useFeedbackPageContext({
    page: 'RunView',
    runId: runId ?? null,
    sessionId: extractedSessionId ?? null,
    activeTab: location.pathname.split('/').pop(),
    runStatus: run?.run_status ?? null,
    runHealth: run?.run_health ?? null,
  });

  // Lap Parsing is intentionally NOT a tab — it's reached from the ⋮ menu
  // (RunActions) for any session type. The `lap-parsing` route still exists
  // (SubRoutes) and is recognised by RUN_SUBROUTE_PATHS below.
  const tabs = useMemo(
    () =>
      RUN_TABS.filter(
        (tab) => tab.chip && (!tab.requiresAuxSensor || showAuxSensor) && (!tab.requiresHts || showHts),
      ).map(({ path, label }) => ({
        path,
        label,
      })),
    [showAuxSensor, showHts],
  );

  // Calculate next and previous runs based on session run order or runs list
  const { nextRunId, previousRunId } = useMemo(() => {
    if (!runId) return { nextRunId: null, previousRunId: null };

    let allRunIds: string[] = [];

    if (isInSessionContext && sessionData) {
      // Session context: collect run IDs from configs
      if (sessionData.configs && Array.isArray(sessionData.configs)) {
        sessionData.configs.forEach((config: any) => {
          if (config.runs && Array.isArray(config.runs)) {
            config.runs.forEach((run: any) => {
              if (run.id) allRunIds.push(run.id);
            });
          }
        });
      }
      if (
        allRunIds.length === 0 &&
        sessionData.run_order &&
        Array.isArray(sessionData.run_order)
      ) {
        allRunIds = sessionData.run_order;
      }
    } else if (!isInSessionContext) {
      // Runs list context: read filtered IDs from localStorage
      try {
        const stored = localStorage.getItem('runListFilteredIds');
        if (stored) allRunIds = JSON.parse(stored);
      } catch { /* ignore malformed data */ }
    }

    if (allRunIds.length === 0) {
      return { nextRunId: null, previousRunId: null };
    }

    const currentIndex = allRunIds.findIndex(
      (id: string) => id === runId,
    );
    if (currentIndex === -1) {
      return { nextRunId: null, previousRunId: null };
    }

    const previousRunId =
      currentIndex > 0 ? allRunIds[currentIndex - 1] : null;
    const nextRunId =
      currentIndex < allRunIds.length - 1
        ? allRunIds[currentIndex + 1]
        : null;

    return { nextRunId, previousRunId };
  }, [sessionData, runId, isInSessionContext]);

  // Check if current path has a tab selected, if not redirect to last visited tab
  useEffect(() => {
    if (!runId) return;

    const currentPath = location.pathname;
    const basePath = getBasePath();

    // Check if we're already on a specific sub-route (including ones whose tab
    // chip may be hidden, e.g. lap-parsing opened from the ⋮ menu).
    const isOnSpecificTab = RUN_SUBROUTE_PATHS.some((p) => currentPath === `${basePath}/${p}`);

    if (!isOnSpecificTab) {
      // Get last visited tab from localStorage
      const lastVisitedTab = localStorage.getItem(LAST_VISITED_TAB_KEY);
      const defaultTab =
        lastVisitedTab && tabs.some((tab) => tab.path === lastVisitedTab) ? lastVisitedTab : 'overview';

      // Navigate to the default tab
      navigate(`${basePath}/${defaultTab}`, { replace: true });
    }
  }, [runId, location.pathname, navigate]);

  // Save current tab to localStorage when it changes
  useEffect(() => {
    if (!runId) return;

    const currentPath = location.pathname;
    const basePath = getBasePath();

    // Check which tab we're currently on
    for (const tab of tabs) {
      if (currentPath === `${basePath}/${tab.path}`) {
        localStorage.setItem(LAST_VISITED_TAB_KEY, tab.path);
        break;
      }
    }
  }, [location.pathname, runId]);

  const RunNavigation = () => {
    if (!isInSessionContext && !nextRunId && !previousRunId) {
      return null;
    }

    const navigateToRun = (targetRunId: string) => {
      const pathParts = location.pathname.split('/');
      const runIndex = pathParts.findIndex((part) => part === 'runs');

      if (runIndex !== -1) {
        // Replace the run ID in the path
        pathParts[runIndex + 1] = targetRunId;
        const newPath = pathParts.join('/');
        navigate(newPath);
      }
    };

    return (
      <div className="cr-inline-flex cr-flex-col cr-items-center cr-gap-1.5">
        <div className="cr-text-xs cr-font-medium cr-text-blue-900">Runs</div>
        <div className="cr-flex cr-gap-2">
          <div className="cr-flex cr-flex-col cr-items-center cr-gap-0.5">
            <button
              onClick={() => previousRunId && navigateToRun(previousRunId)}
              disabled={!previousRunId}
              className="cr-flex cr-items-center cr-justify-center cr-rounded-lg cr-bg-cirrusBlue-100
                cr-p-2 cr-text-blue-900 cr-transition-colors hover:cr-bg-gray-350
                disabled:cr-cursor-not-allowed disabled:cr-opacity-40"
              title="Previous Run"
            >
              <HiChevronLeft className="cr-h-5 cr-w-5" />
            </button>
            <div className="cr-text-xs cr-text-blue-900">Prev</div>
          </div>
          <div className="cr-flex cr-flex-col cr-items-center cr-gap-0.5">
            <button
              onClick={() => nextRunId && navigateToRun(nextRunId)}
              disabled={!nextRunId}
              className="cr-flex cr-items-center cr-justify-center cr-rounded-lg cr-bg-cirrusBlue-100
                cr-p-2 cr-text-blue-900 cr-transition-colors hover:cr-bg-gray-350
                disabled:cr-cursor-not-allowed disabled:cr-opacity-40"
              title="Next Run"
            >
              <HiChevronRight className="cr-h-5 cr-w-5" />
            </button>
            <div className="cr-text-xs cr-text-blue-900">Next</div>
          </div>
        </div>
      </div>
    );
  };

  if (!runId) {
    return (
      <div className="cr-flex cr-h-64 cr-items-center cr-justify-center">
        <div className="cr-text-gray-500">No run ID provided</div>
      </div>
    );
  }

  return (
    <div className="cr-w-full">
      <SectionHeader
        title={'Run Details'}
        isLoading={isRunsFetching}
        backLink={getBackLink()}
        actions={runId ? <RunActions runId={runId} run={run} /> : null}
       
      />
      <div style={{ marginTop: '-1.5rem' }}>
        {/* Run Navigation */}
        <div className="cr-mb-0 cr-flex cr-items-center cr-justify-between cr-gap-8">
          <div className="cr-flex-1">
            {run && <RunSummary run={run} calibration={calibration} isLoading={isRunsPending} />}
          </div>
          <RunNavigation />
        </div>
      </div>

      <div className="cr-mt-2">
        <div className="cr-mb-6 cr-flex cr-items-center cr-justify-between">
          <TabMenu tabs={tabs} basePath={getBasePath()} />
          <div className="cr-flex cr-items-center cr-gap-x-2">
            {runId && <DownloadCSVButton runId={runId} />}
            {runId && <DownloadFITButton runId={runId} />}
          </div>
        </div>

        <Outlet context={{ run, isRunsPending, isRunsFetching, calibration, sessionData }} />
      </div>
    </div>
  );
}
